import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Plus, 
  Download, 
  MoreHorizontal,
  User,
  Users,
  Shield,
  Mail,
  Calendar,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  ChevronUp,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { Pagination } from '@/components/ui/pagination';
import { apiRequest } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  permissionLevel: 'app_admin' | 'hub_super_user' | 'hub_admin' | 'project_admin' | 'tender_user';
  hubName: string;
  country: string;
  dataCentre: string;
  projectName: string;
  status: 'active' | 'inactive';
  lastLoginAt: string;
  createdAt: string;
  isActive: boolean;
  canAccessMainApp: boolean;
  canAccessTendererPortal: boolean;
  canViewTenders: boolean;
  canManageTenders: boolean;
}

const AdminUsers: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // 从真实数据库获取用户数据
  const { data: users = [], isLoading, error, refetch } = useQuery<User[]>({
    queryKey: ['/api/admin/app-users'],
    queryFn: async () => {
      const data = await apiRequest('/api/admin/app-users');
      return data.users || [];
    },
    // 每10秒自动刷新一次，确保数据同步
    refetchInterval: 10000,
    // 当窗口重新获得焦点时刷新
    refetchOnWindowFocus: true,
    // 当网络重新连接时刷新
    refetchOnReconnect: true,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [hubFilter, setHubFilter] = useState<string>('all');
  const [dataCentreFilter, setDataCentreFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isScrolled, setIsScrolled] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'name', label: 'Name', visible: true, sortable: true, width: 'w-48', frozen: true },
    { key: 'email', label: 'Email', visible: true, sortable: true, width: 'w-64' },
    { key: 'permissionLevel', label: 'Role', visible: true, sortable: true, width: 'w-40' },
    { key: 'hubName', label: 'Hub', visible: true, sortable: true, width: 'w-48' },
    { key: 'country', label: 'Country', visible: true, sortable: true, width: 'w-32' },
    { key: 'dataCentre', label: 'Data Centre', visible: true, sortable: true, width: 'w-32' },
    { key: 'projectName', label: 'Project', visible: true, sortable: true, width: 'w-48' },
    { key: 'status', label: 'Status', visible: true, sortable: true, width: 'w-24' },
    { key: 'lastLoginAt', label: 'Last Login', visible: true, sortable: true, width: 'w-40' },
    { key: 'createdAt', label: 'Created', visible: true, sortable: true, width: 'w-40' },
  ]);

  // 监听 Hub 更新事件，自动刷新 Users 数据
  useEffect(() => {
    const handleHubUpdate = () => {
      console.log('🔄 Hub updated, refreshing users data...');
      refetch();
    };

    // 监听自定义事件
    window.addEventListener('hub-updated', handleHubUpdate);
    
    // 监听 storage 变化（当 Hub 数据更新时）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'hub-data-updated') {
        console.log('🔄 Hub data updated in storage, refreshing users...');
        refetch();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('hub-updated', handleHubUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refetch]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, hubFilter, dataCentreFilter]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.hubName && user.hubName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? user.isActive : !user.isActive);
    const matchesRole = roleFilter === 'all' || user.permissionLevel === roleFilter;
    const matchesHub = hubFilter === 'all' || user.hubName === hubFilter;
    const matchesDataCentre = dataCentreFilter === 'all' || user.dataCentre === dataCentreFilter;
    
    return matchesSearch && matchesStatus && matchesRole && matchesHub && matchesDataCentre;
  }).sort((a, b) => {
    let aValue: any = a[sortField as keyof User];
    let bValue: any = b[sortField as keyof User];
    
    // Handle special cases
    if (sortField === 'name') {
      aValue = `${a.firstName} ${a.lastName}`;
      bValue = `${b.firstName} ${b.lastName}`;
    }
    
    // Convert to string for comparison
    aValue = String(aValue || '').toLowerCase();
    bValue = String(bValue || '').toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  // Pagination logic
  const totalItems = filteredUsers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);



  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ✅ 新增：两个滚动容器的 ref
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  // ✅ 新增：同步函数（顶部 ⇄ 主体）
  const syncFromTop = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把顶部滚动条的 scrollLeft 同步给表格容器
    bodyScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const syncFromBody = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    // 把表格容器的 scrollLeft 同步给顶部滚动条
    topScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    // 保留你原来的水平滚动阴影逻辑
    setIsScrolled(bodyScrollRef.current.scrollLeft > 0);
  };

  // ✅ 添加CSS来隐藏顶部滚动条的滚动条（Webkit浏览器）
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-top-scrollbar-users';
    style.textContent = `
      .hide-top-scrollbar-users::-webkit-scrollbar {
        display: none;
      }
    `;
    
    // 移除已存在的样式
    const existingStyle = document.getElementById('hide-top-scrollbar-users');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
    
    return () => {
      const styleToRemove = document.getElementById('hide-top-scrollbar-users');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setIsScrolled(target.scrollLeft > 0);
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
    }
  };

  const getRoleBadge = (permissionLevel: string) => {
    switch (permissionLevel) {
      case 'app_admin':
        return <Badge className="bg-purple-100 text-purple-800"><Shield className="w-3 h-3 mr-1" />App Admin</Badge>;
      case 'hub_super_user':
        return <Badge className="bg-purple-100 text-purple-800"><Shield className="w-3 h-3 mr-1" />Hub Super User</Badge>;
      case 'hub_admin':
        return <Badge className="bg-blue-100 text-blue-800"><Shield className="w-3 h-3 mr-1" />Hub Admin</Badge>;
      case 'project_admin':
        return <Badge className="bg-green-100 text-green-800"><User className="w-3 h-3 mr-1" />Project Admin</Badge>;
      case 'tender_user':
        return <Badge className="bg-orange-100 text-orange-800"><User className="w-3 h-3 mr-1" />Tender User</Badge>;
      default:
        return <Badge variant="secondary">{permissionLevel}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Render cell content based on column key
  const renderCell = (user: User, columnKey: string) => {
    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src="" />
              <AvatarFallback className="bg-blue-100 text-blue-600 text-xs">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.firstName} {user.lastName}</div>
            </div>
          </div>
        );
      case 'email':
        return (
          <span 
            className="text-sm text-gray-600 truncate block" 
            title={user.email}
          >
            {user.email}
          </span>
        );
      case 'permissionLevel':
        return getRoleBadge(user.permissionLevel);
      case 'hubName':
        return <span>{user.hubName || 'N/A'}</span>;
      case 'country':
        return <span className="font-medium text-blue-600">{user.country || 'N/A'}</span>;
      case 'dataCentre':
        return <span className="font-medium text-purple-600">{user.dataCentre || 'N/A'}</span>;
      case 'projectName':
        // Hub Super User and Hub Admin don't need to show project
        if (user.permissionLevel === 'hub_super_user' || user.permissionLevel === 'hub_admin') {
          return <span className="text-gray-400">N/A</span>;
        }
        return <span>{user.projectName || 'N/A'}</span>;
      case 'status':
        return getStatusBadge(user.isActive);
      case 'lastLoginAt':
        return <span className="text-sm text-gray-600">{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}</span>;
      case 'createdAt':
        return <span className="text-sm text-gray-600">{formatDate(user.createdAt)}</span>;
      default:
        return null;
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Role', 'Hub', 'Data Centre', 'Project', 'Status', 'Last Login', 'Created'];
    const csvContent = [
      headers.join(','),
      ...filteredUsers.map(user => [
        `${user.firstName} ${user.lastName}`,
        user.email,
        user.permissionLevel,
        user.hubName || 'N/A',
        user.dataCentre || 'N/A',
        (user.permissionLevel === 'hub_super_user' || user.permissionLevel === 'hub_admin') ? 'N/A' : (user.projectName || 'N/A'),
        user.isActive ? 'Active' : 'Inactive',
        user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never',
        formatDate(user.createdAt)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const uniqueHubs = Array.from(new Set(users.map(user => user.hubName).filter(Boolean)));
  const uniqueDataCentres = Array.from(new Set(users.map(user => user.dataCentre).filter(Boolean)));

  // 处理加载状态
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  // 处理错误状态
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium">Error loading users</div>
            <p className="mt-2 text-gray-600">Please try again later</p>
            <Button 
              onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] })}
              className="mt-4"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">


      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(user => user.isActive).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Admins</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(user => user.permissionLevel === 'hub_admin' || user.permissionLevel === 'hub_super_user' || user.permissionLevel === 'app_admin').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tenderers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(user => user.permissionLevel === 'tender_user').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="app_admin">App Admin</SelectItem>
                  <SelectItem value="hub_super_user">Hub Super User</SelectItem>
                  <SelectItem value="hub_admin">Hub Admin</SelectItem>
                  <SelectItem value="project_admin">Project Admin</SelectItem>
                  <SelectItem value="tender_user">Tender User</SelectItem>
                </SelectContent>
              </Select>
              <Select value={hubFilter} onValueChange={setHubFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by hub" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hubs</SelectItem>
                  {uniqueHubs.map(hub => (
                    <SelectItem key={hub} value={hub}>{hub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dataCentreFilter} onValueChange={setDataCentreFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by data centre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Data Centres</SelectItem>
                  {uniqueDataCentres.map(dataCentre => (
                    <SelectItem key={dataCentre} value={dataCentre}>{dataCentre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden" style={{marginBottom: '8px'}}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900">Users Management</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              <ColumnConfigDropdown 
                columns={columns}
                onColumnsChange={setColumns}
              />
              
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">

          {/* ✅ 新增：顶部"镜像滚动条"——完全隐藏但保持滚动功能 */}
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="w-full overflow-x-auto hide-top-scrollbar-users"
            style={{ 
              height: 0,
              scrollbarGutter: 'stable',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div style={{ width: '1400px', height: 1 }} />
          </div>

          {/* ✅ 修改：主滚动容器 */}
          <div
            ref={bodyScrollRef}
            className="overflow-x-auto overflow-y-scroll"
            style={{
              height: 'calc(100vh - 160px)',
              maxWidth: '100%',
            }}
            onScroll={syncFromBody}
          >
            {/* 表格本体 */}
            <Table className="border-collapse" style={{ minWidth: '1400px', width: '1400px' }}>
              {/* ❗️注意：这里不要再给 TableHeader 外层加 sticky，
                   让每个 <TableHead/>（th）自己 sticky（我们在组件里已经改好了）
              */}
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead 
                      key={column.key} 
                      className={`${column.width} py-2${column.sortable ? ' cursor-pointer hover:bg-gray-50' : ''}${column.frozen ? ' sticky left-0 z-40 bg-gray-50 shadow-lg' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{column.label}</span>
                        {column.sortable && sortField === column.key && (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key} className={`${column.width} py-2${column.frozen ? ' sticky left-0 z-20 bg-white shadow-lg hover:bg-white' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}>
                        {renderCell(user, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* 完全移除空白行，让表格更紧凑 */}
              </TableBody>
            </Table>
          </div>

          {/* ✅ 分页区域保持在滚动条下面（也就是"滚动条在分页上方"） */}
          <div className="bg-white">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;
